import { useQuery } from '@tanstack/react-query';
import { fetchJSON } from '../lib/api-client';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';

interface RiskScenario { startReturn: number; endReturn: number; maxDD: number }

interface RiskMetrics {
  symbol: string;
  asOf: string;
  vol30d: number;
  vol90d: number;
  vol252d: number;
  beta?: number;
  betaVsSpx?: number;
  betaVsTwii?: number;
  maxDrawdown5y: number;
  maxDDRecoveryDays5y?: number;
  maxDrawdown10y?: number;
  scenarios: { gfc2008?: RiskScenario; covid2020?: RiskScenario; bear2022?: RiskScenario };
  avgDailyTurnoverUSD30d: number;
  avgDailyTurnover30d: number;
  turnoverCurrency: 'USD' | 'TWD';
  earningsDayMoveAvg?: number;
  earningsDayMoveMedian?: number;
  warnings?: string[];
}

function pct(n: number | undefined, digits = 1): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(digits)}%`;
}

function num(n: number | undefined, digits = 2): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function money(n: number | undefined, currency = 'USD'): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e9) return `${currency} ${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${currency} ${(n / 1e6).toFixed(1)}M`;
  return `${currency} ${Math.round(n).toLocaleString()}`;
}

export function RiskDashboardPanel({ symbol }: { symbol: string }) {
  const q = useQuery({
    queryKey: ['risk-metrics', symbol],
    queryFn: () => fetchJSON<RiskMetrics>(`/api/risk/${encodeURIComponent(symbol)}`),
    enabled: !!symbol,
    staleTime: 30 * 60_000,
    retry: 1,
  });

  if (q.isLoading) {
    return <div className="card"><div className="text-(--text-muted) text-sm py-4 text-center">載入風險 evidence…</div></div>;
  }
  if (q.error) {
    const err = q.error as Error;
    return <div className="card"><div className={isDelistedError(err) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(err) ? DELISTED_MESSAGE : `無法取得風險 evidence：${err.message}`}</div></div>;
  }
  if (!q.data) return null;

  const r = q.data;
  const beta = r.betaVsTwii ?? r.betaVsSpx ?? r.beta;
  const betaLabel = r.betaVsTwii != null ? 'Beta（相對加權）' : r.betaVsSpx != null ? 'Beta（相對標普）' : 'Beta';

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-bold text-(--text-primary) text-sm">🛡 風險依據</div>
          <div className="text-[10px] text-(--text-muted) tnum">{r.symbol} · {new Date(r.asOf).toLocaleString('zh-TW', { hour12: false })}</div>
        </div>
        <span className="rounded border border-(--border) px-1.5 py-0.5 text-[10px] text-(--text-muted)">純資料</span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="30日波動" value={pct(r.vol30d)} accent={r.vol30d > 0.5 ? 'down' : r.vol30d > 0.35 ? 'flat' : 'up'} />
        <Stat label="年波動" value={pct(r.vol252d)} />
        <Stat label={betaLabel} value={num(beta, 2)} accent={beta != null && beta > 1.4 ? 'down' : 'flat'} />
        <Stat label="5年最大回撤" value={pct(r.maxDrawdown5y)} accent={r.maxDrawdown5y < -0.35 ? 'down' : 'flat'} />
        <Stat label="復原天數" value={r.maxDDRecoveryDays5y != null ? `${r.maxDDRecoveryDays5y} 天` : '未收復'} />
        <Stat label="30日成交額" value={money(r.avgDailyTurnoverUSD30d)} accent={r.avgDailyTurnoverUSD30d < 1_000_000 ? 'down' : 'up'} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
        <Scenario label="金融海嘯 08" data={r.scenarios.gfc2008} />
        <Scenario label="疫情 20" data={r.scenarios.covid2020} />
        <Scenario label="熊市 22" data={r.scenarios.bear2022} />
      </div>

      {(r.earningsDayMoveAvg != null || (r.warnings?.length ?? 0) > 0) && (
        <div className="rounded border border-(--border) bg-(--bg-base) px-2 py-1.5 text-[11px] text-(--text-secondary) leading-relaxed">
          {r.earningsDayMoveAvg != null && <div>財報日平均隔日波動：<b className="tnum">{pct(r.earningsDayMoveAvg)}</b></div>}
          {r.warnings?.slice(0, 2).map((w) => <div key={w} className="text-(--warn)">⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'flat' }: { label: string; value: string; accent?: 'up' | 'down' | 'flat' }) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="rounded bg-(--bg-base) border border-(--border) px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-(--text-muted)">{label}</div>
      <div className={`text-xs font-bold tnum ${cls}`}>{value}</div>
    </div>
  );
}

function Scenario({ label, data }: { label: string; data?: RiskScenario }) {
  return (
    <div className="rounded bg-(--bg-base) border border-(--border) px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-(--text-muted)">{label}</div>
      <div className="text-xs font-bold tnum text-(--text-primary)">{data ? pct(data.endReturn) : '—'}</div>
      <div className="text-[10px] text-(--text-muted)">回撤 {data ? pct(data.maxDD) : '—'}</div>
    </div>
  );
}
